import { Uint8ArrayReader, ZipReader, type Entry, type FileEntry } from '@zip.js/zip.js';
import * as XLSX from 'xlsx';

export const PROJECT_PLAN_FILE_LIMIT = 5 * 1024 * 1024;
export const PROJECT_PLAN_EXPANDED_LIMIT = 20 * 1024 * 1024;
export const PROJECT_PLAN_ZIP_ENTRY_LIMIT = 128;
export const PROJECT_PLAN_COMPRESSION_RATIO_LIMIT = 100;

const ACTIVE_PART = /(?:\.bin$|(?:^|\/)(?:vbaProject\.bin|externalLinks(?:\/|$)|connections\.xml|queryTables(?:\/|$)|embeddings(?:\/|$)|oleObjects?(?:\/|$)|activeX(?:\/|$)|macroSheets?(?:\/|$)|dialogSheets?(?:\/|$)|ddeLinks?(?:\/|$)|webextensions?(?:\/|$)|taskpanes?(?:\/|$)|comments\d*\.xml|threadedComments(?:\/|$)))/i;
const ACTIVE_CONTENT_TYPE = /(?:vbaProject|macroEnabled|macroSheet|dialogSheet|externalLink|connections?|queryTable|oleObject|activeX|webextension|taskpane|comments?|threadedComments?|ddeLink)/i;
const ACTIVE_RELATIONSHIP_TYPE = /\/(?:control|oleObject|package|externalLink|queryTable|connections|vbaProject|comments?|threadedComments?|hyperlink|activeXControl(?:Binary)?|webextension(?:taskpanes)?|taskpane)$/i;

function decodeXmlSecurityEntities(value: string): string {
  const entity = /&(?:amp|lt|gt|quot|apos|#\d+|#[xX][0-9A-Fa-f]+);/g;
  if (value.replace(entity, '').includes('&')) throw new Error('XLSX содержит некорректную XML-сущность');
  const decoded = value.replace(entity, (match) => {
    if (/^&#[xX]/.test(match)) {
      const point = Number.parseInt(match.slice(3, -1), 16);
      if (!Number.isSafeInteger(point) || point < 0 || point > 0x10ffff) throw new Error('XLSX содержит недопустимую XML-сущность');
      return String.fromCodePoint(point);
    }
    if (/^&#/.test(match)) {
      const point = Number.parseInt(match.slice(2, -1), 10);
      if (!Number.isSafeInteger(point) || point < 0 || point > 0x10ffff) throw new Error('XLSX содержит недопустимую XML-сущность');
      return String.fromCodePoint(point);
    }
    return ({ '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&apos;': "'" } as Record<string, string>)[match];
  });
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/.test(decoded)) {
    throw new Error('XLSX содержит недопустимую XML-сущность');
  }
  for (let index = 0; index < decoded.length; index++) {
    const code = decoded.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = decoded.charCodeAt(++index);
      if (!Number.isFinite(next) || next < 0xdc00 || next > 0xdfff) throw new Error('XLSX содержит недопустимую XML-сущность');
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      throw new Error('XLSX содержит недопустимую XML-сущность');
    }
  }
  return decoded;
}

export interface XmlTagToken {
  value: string;
  index: number;
  end: number;
}

export function scanXmlTags(xml: string, label = 'XML'): XmlTagToken[] {
  const tags: XmlTagToken[] = [];
  for (let start = xml.indexOf('<'); start >= 0; start = xml.indexOf('<', start + 1)) {
    let quote = '';
    let end = start + 1;
    for (; end < xml.length; end++) {
      const character = xml[end];
      if (quote) {
        if (character === quote) quote = '';
      } else if (character === '"' || character === "'") {
        quote = character;
      } else if (character === '>') {
        break;
      }
    }
    if (end >= xml.length || quote) throw new Error(`${label} содержит некорректный XML-тег`);
    tags.push({ value: xml.slice(start, end + 1), index: start, end: end + 1 });
    start = end;
  }
  return tags;
}

function assertSafeXmlPart(name: string, xml: string, archiveParts?: ReadonlySet<string>): void {
  if (/<!DOCTYPE|<!ENTITY/i.test(xml)) throw new Error(`XLSX XML ${name}: DOCTYPE и ENTITY запрещены`);
  if (/<\s*(?:[A-Za-z_][\w.-]*:)?definedName\b/i.test(xml)) {
    throw new Error(`XLSX содержит именованную формулу: ${name}`);
  }
  if (/<\s*(?:[A-Za-z_][\w.-]*:)?hyperlink\b/i.test(xml)) {
    throw new Error(`XLSX содержит гиперссылку: ${name}`);
  }
  if (/<\s*(?:[A-Za-z_][\w.-]*:)?(?:f|formula[12]?|calculatedColumnFormula|totalsRowFormula)\b/i.test(xml)) {
    throw new Error(`XLSX содержит формулу: ${name}`);
  }
  for (const { value: rawTag } of scanXmlTags(xml, 'XLSX')) {
    const tag = decodeXmlSecurityEntities(rawTag);
    if (/^<\s*(?:[A-Za-z_][\w.-]*:)?include(?=\s|\/?>)/i.test(tag)) {
      throw new Error(`XLSX XML ${name}: XInclude запрещён`);
    }
    if (/\s(?:[A-Za-z_][\w.-]*:)?(?:href|schemaLocation|noNamespaceSchemaLocation)\s*=/i.test(tag)) {
      throw new Error(`XLSX XML ${name}: внешние href и schemaLocation запрещены`);
    }
    const targetMode = /\bTargetMode\s*=\s*["']([^"']*)["']/i.exec(tag)?.[1];
    if (targetMode?.toLowerCase() === 'external') throw new Error(`XLSX содержит внешнюю связь: ${name}`);
    for (const match of Array.from(tag.matchAll(/\b(ContentType|Type)\s*=\s*["']([^"']*)["']/gi))) {
      if (match[1].toLowerCase() === 'contenttype' && /^<\s*(?:[A-Za-z_][\w.-]*:)?Default\b/i.test(tag)) {
        const extension = /\bExtension\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1]?.toLowerCase();
        if (!extension || !archiveParts || !Array.from(archiveParts).some((part) => part.endsWith(`.${extension}`))) continue;
      }
      const active = match[1].toLowerCase() === 'contenttype'
        ? ACTIVE_CONTENT_TYPE.test(match[2])
        : ACTIVE_RELATIONSHIP_TYPE.test(match[2]);
      if (active) throw new Error(`XLSX содержит запрещённый активный тип или связь: ${name}`);
    }
  }
}

function decodeXmlPart(name: string, data: ArrayBuffer): string {
  const bytes = new Uint8Array(data);
  let encoding = 'utf-8';
  let offset = 0;
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    encoding = 'utf-16le';
    offset = 2;
  } else if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    encoding = 'utf-16be';
    offset = 2;
  } else if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    offset = 3;
  }
  let xml: string;
  try {
    xml = new TextDecoder(encoding, { fatal: true }).decode(bytes.subarray(offset));
  } catch (error) {
    throw new Error(`XLSX содержит XML в некорректной кодировке: ${name}: ${error instanceof Error ? error.message : String(error)}`);
  }
  if (xml.includes('\u0000')) throw new Error(`XLSX содержит XML в некорректной кодировке: ${name}`);
  const declared = /^\s*<\?xml\b[^>]*\bencoding\s*=\s*["']([^"']+)["']/i.exec(xml)?.[1]?.toLowerCase();
  if (declared && !(
    (encoding === 'utf-8' && ['utf-8', 'us-ascii'].includes(declared))
    || (encoding.startsWith('utf-16') && ['utf-16', encoding].includes(declared))
  )) {
    throw new Error(`XLSX XML ${name}: объявленная кодировка ${declared} не соответствует данным`);
  }
  return xml;
}

function asBytes(data: ArrayBuffer): Uint8Array {
  return new Uint8Array(data);
}

function assertSafeEntryName(name: string, seen: Set<string>): void {
  const normalized = String(name || '').replace(/\\/g, '/');
  const canonical = normalized.toLowerCase();
  if (!normalized || normalized.startsWith('/') || /^[a-z]:/i.test(normalized)) {
    throw new Error(`XLSX содержит недопустимый ZIP-путь: ${name}`);
  }
  const parts = normalized.split('/');
  if (parts.some((part) => part === '..' || part === '.')) {
    throw new Error(`XLSX содержит обход ZIP-пути: ${name}`);
  }
  if (seen.has(canonical)) throw new Error(`XLSX содержит дублирующий ZIP-путь: ${name}`);
  seen.add(canonical);
  if (ACTIVE_PART.test(normalized)) throw new Error(`XLSX содержит запрещённую активную часть: ${name}`);
}

async function extractEntryBounded(
  entry: FileEntry,
  collect: boolean,
  addActualSize: (size: number) => void,
): Promise<ArrayBuffer | undefined> {
  const chunks: Uint8Array[] = [];
  let collectedSize = 0;
  let limitError: Error | undefined;
  const abortController = new AbortController();
  const writable = new WritableStream<Uint8Array>({
    write(chunk) {
      try {
        addActualSize(chunk.byteLength);
      } catch (error) {
        limitError = error instanceof Error ? error : new Error(String(error));
        abortController.abort(limitError);
        throw limitError;
      }
      if (collect) {
        const copy = chunk.slice();
        chunks.push(copy);
        collectedSize += copy.byteLength;
      }
    },
  });
  try {
    await entry.getData(writable, {
      checkSignature: true,
      checkAmbiguity: true,
      signal: abortController.signal,
    });
  } catch (error) {
    if (limitError) throw limitError;
    throw new Error(`XLSX содержит неоднозначные или перекрывающиеся ZIP-части: ${error instanceof Error ? error.message : String(error)}`);
  }
  if (!collect) return undefined;
  const content = new Uint8Array(collectedSize);
  let offset = 0;
  chunks.forEach((chunk) => {
    content.set(chunk, offset);
    offset += chunk.byteLength;
  });
  return content.buffer;
}

export async function validateXlsxArchive(data: ArrayBuffer): Promise<void> {
  const bytes = asBytes(data);
  if (bytes.byteLength > PROJECT_PLAN_FILE_LIMIT) {
    throw new Error('Размер XLSX превышает 5 MiB');
  }
  if (bytes.byteLength < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    throw new Error('Файл не является корректным ZIP/XLSX архивом');
  }

  const reader = new ZipReader(new Uint8ArrayReader(bytes), { checkSignature: true, strictness: 'strict' });
  try {
    let entries: Entry[];
    try {
      entries = await reader.getEntries();
    } catch (error) {
      throw new Error(`Некорректный ZIP/XLSX архив: ${error instanceof Error ? error.message : String(error)}`);
    }
    if (entries.length > PROJECT_PLAN_ZIP_ENTRY_LIMIT) {
      throw new Error(`XLSX содержит более ${PROJECT_PLAN_ZIP_ENTRY_LIMIT} ZIP-частей`);
    }

    const seen = new Set<string>();
    let compressed = 0;
    let expanded = 0;
    for (const entry of entries) {
      assertSafeEntryName(entry.filename, seen);
      if (entry.encrypted) throw new Error(`XLSX содержит зашифрованную ZIP-часть: ${entry.filename}`);
      if (entry.zip64) throw new Error(`XLSX содержит запрещённую ZIP64-часть: ${entry.filename}`);
      const packed = Number(entry.compressedSize || 0);
      const unpacked = Number(entry.uncompressedSize || 0);
      compressed += packed;
      expanded += unpacked;
      if (expanded > PROJECT_PLAN_EXPANDED_LIMIT) throw new Error('Распакованный размер XLSX превышает 20 MiB');
      if (unpacked > 0 && unpacked / Math.max(1, packed) > PROJECT_PLAN_COMPRESSION_RATIO_LIMIT) {
        throw new Error(`Подозрительная степень сжатия XLSX: ${entry.filename}`);
      }
    }
    if (expanded > 0 && expanded / Math.max(1, compressed) > PROJECT_PLAN_COMPRESSION_RATIO_LIMIT) {
      throw new Error('Подозрительная суммарная степень сжатия XLSX');
    }

    let actualExpanded = 0;
    for (const entry of entries) {
      if (entry.directory) continue;
      const inspectXml = /(?:\.xml|\.rels)$/i.test(entry.filename);
      const content = await extractEntryBounded(entry as FileEntry, inspectXml, (size) => {
        actualExpanded += size;
        if (actualExpanded > PROJECT_PLAN_EXPANDED_LIMIT) {
          throw new Error('Фактический распакованный размер XLSX превышает 20 MiB');
        }
      });
      if (/\.rels$/i.test(entry.filename)) {
        if (!content) throw new Error(`XLSX не удалось проверить XML-часть: ${entry.filename}`);
        const xml = decodeXmlPart(entry.filename, content);
        assertSafeXmlPart(entry.filename, xml, seen);
      } else if (/\.xml$/i.test(entry.filename)) {
        if (!content) throw new Error(`XLSX не удалось проверить XML-часть: ${entry.filename}`);
        assertSafeXmlPart(entry.filename, decodeXmlPart(entry.filename, content), seen);
      }
    }
  } finally {
    await reader.close();
  }
}

export interface WorkbookValidationOptions {
  allowedSheets?: readonly string[];
}

export function validateWorkbookContent(
  workbook: XLSX.WorkBook,
  options: WorkbookValidationOptions = {},
): void {
  const allowedNames = options.allowedSheets;
  const allowed = allowedNames ? new Set(allowedNames) : null;
  for (const name of workbook.SheetNames || []) {
    if (allowed && !allowed.has(name)) throw new Error(`XLSX содержит неожиданный лист: ${name}`);
  }
  if (allowedNames) {
    if (new Set(workbook.SheetNames).size !== workbook.SheetNames.length) throw new Error('XLSX содержит дублирующий лист');
    if (workbook.SheetNames.length !== allowedNames.length) throw new Error('XLSX содержит неполный набор листов');
    if (workbook.SheetNames.some((name, index) => name !== allowedNames[index])) {
      throw new Error('XLSX нарушает канонический порядок листов');
    }
  }
  const sheetMeta = workbook.Workbook?.Sheets || [];
  sheetMeta.forEach((meta, index) => {
    if (Number(meta?.Hidden || 0) !== 0) {
      throw new Error(`XLSX содержит скрытый лист: ${workbook.SheetNames[index] || index + 1}`);
    }
  });
  if ((workbook.Workbook?.Names || []).length) {
    throw new Error('XLSX содержит именованную формулу или диапазон');
  }

  for (const name of workbook.SheetNames || []) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    Object.keys(sheet).forEach((address) => {
      if (address.startsWith('!')) return;
      const cell = sheet[address] as XLSX.CellObject & { F?: string; h?: string };
      if (cell.f || cell.F) throw new Error(`XLSX содержит формулу: ${name}!${address}`);
      if (cell.l) throw new Error(`XLSX содержит гиперссылку: ${name}!${address}`);
      if (cell.c?.length) throw new Error(`XLSX содержит комментарий: ${name}!${address}`);
      if (cell.h) throw new Error(`XLSX содержит HTML: ${name}!${address}`);
      if (typeof cell.v === 'string' && Array.from(cell.v).length > 32767) {
        throw new Error(`XLSX ячейка превышает лимит 32767 символов: ${name}!${address}`);
      }
      if (typeof cell.v === 'string' && /<\/?[A-Za-z][^>]*>/.test(cell.v)) {
        throw new Error(`XLSX содержит HTML-разметку: ${name}!${address}`);
      }
    });
  }
}

export async function readSafeWorkbook(
  data: ArrayBuffer,
  options: WorkbookValidationOptions = {},
): Promise<XLSX.WorkBook> {
  await validateXlsxArchive(data);
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(data, {
      type: 'array',
      cellFormula: true,
      cellHTML: false,
      cellNF: false,
      cellStyles: false,
      bookVBA: true,
    });
  } catch (error) {
    throw new Error(`Не удалось прочитать XLSX: ${error instanceof Error ? error.message : String(error)}`);
  }
  if ((workbook as XLSX.WorkBook & { vbaraw?: unknown }).vbaraw) {
    throw new Error('XLSX содержит макрос VBA');
  }
  validateWorkbookContent(workbook, options);
  return workbook;
}

export async function readXlsxPreview(data: ArrayBuffer): Promise<string[][]> {
  const workbook = await readSafeWorkbook(data);
  const firstName = workbook.SheetNames[0];
  const sheet = firstName ? workbook.Sheets[firstName] : null;
  if (!sheet) return [];
  const sourceRange = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']) : null;
  if (!sourceRange) return [];
  const range = XLSX.utils.encode_range({
    s: sourceRange.s,
    e: {
      r: Math.min(sourceRange.e.r, sourceRange.s.r + 199),
      c: Math.min(sourceRange.e.c, sourceRange.s.c + 49),
    },
  });
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: false,
    blankrows: false,
    defval: '',
    range,
  });
  return rows.map((row) => row.map((value) => String(value ?? '').slice(0, 2000)));
}
